import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useCreateSpeedDial, useUpdateSpeedDial } from "@/lib/queries";
import type { SpeedDial } from "@/lib/types";

/**
 * Add a speed dial, or edit one (ADR-065): a name and the number it rings. The server checks the
 * number is a US or Canadian one in E.164 and refuses a second row with the same number.
 */

const schema = z.object({
  label: z.string().trim().min(1, "Required").max(80, "At most 80 characters"),
  number: z.string().trim().min(1, "Required").max(40, "At most 40 characters"),
  sort_order: z.coerce.number().int().min(0).max(10_000),
});
type FormValues = z.infer<typeof schema>;

const BLANK: FormValues = { label: "", number: "", sort_order: 0 };

export function SpeedDialFormDialog({
  open, onOpenChange, speedDial,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  speedDial?: SpeedDial;
}) {
  const toast = useToast();
  const create = useCreateSpeedDial();
  const update = useUpdateSpeedDial();
  const [formError, setFormError] = React.useState<string | null>(null);
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: BLANK });
  const { errors } = form.formState;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(
      speedDial
        ? { label: speedDial.label, number: speedDial.number, sort_order: speedDial.sort_order }
        : BLANK,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, speedDial]);

  const onSubmit = form.handleSubmit(async (submitted) => {
    setFormError(null);
    try {
      if (speedDial) {
        await update.mutateAsync({ id: speedDial.id, ...submitted });
        toast.success("Speed dial saved", submitted.label);
      } else {
        await create.mutateAsync(submitted);
        toast.success("Speed dial added", submitted.label);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the speed dial.");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{speedDial ? "Edit speed dial" : "New speed dial"}</DialogTitle>
          <DialogDescription>
            A name reps pick from Add participant on a call, and the number it rings.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field label="Name" htmlFor="speed-dial-label" required error={errors.label?.message}>
              <Input
                id="speed-dial-label"
                autoComplete="off"
                placeholder="Collins"
                invalid={Boolean(errors.label)}
                {...form.register("label")}
              />
            </Field>
            <Field
              label="Number"
              htmlFor="speed-dial-number"
              required
              error={errors.number?.message}
              hint="A US or Canadian number, as dialled."
            >
              <Input
                id="speed-dial-number"
                autoComplete="off"
                inputMode="tel"
                placeholder="+1 952 994 1286"
                invalid={Boolean(errors.number)}
                {...form.register("number")}
              />
            </Field>
            <Field
              label="Order"
              htmlFor="speed-dial-order"
              error={errors.sort_order?.message}
              hint="Lowest first on the phone."
            >
              <Input
                id="speed-dial-order"
                type="number"
                min={0}
                className="w-28"
                invalid={Boolean(errors.sort_order)}
                {...form.register("sort_order")}
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {speedDial ? "Save" : "Add speed dial"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
